import React from 'react'
import { motion } from 'framer-motion'
import { UserPlus, CreditCard, Ticket, Trophy, ArrowRight } from 'lucide-react'

const HowItWorks = () => {
  const steps = [
    {
      icon: UserPlus,
      title: "Regístrate",
      description: "Crea tu cuenta gratuita en segundos. Solo necesitas tu email y una contraseña segura.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Ticket,
      title: "Elige tu Membresía",
      description: "Explora nuestras membresías activas y selecciona la que más te guste. Cada membresía tiene 100,000 números únicos.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: CreditCard,
      title: "Compra Boletos",
      description: "Selecciona cuántos boletos quieres y paga de forma segura. Tus números se reservan instantáneamente.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Trophy,
      title: "¡Gana Premios!",
      description: "Espera la membresía y cruza los dedos. Si tu número sale ganador, recibirás tu premio inmediatamente.",
      color: "from-yellow-500 to-orange-500"
    }
  ]

  return (
    <section id="como-funciona" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            ¿Cómo
            <span className="gradient-text block">Funciona?</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Participar en nuestras membresías es súper fácil. Solo sigue estos 4 simples pasos
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 transform -translate-y-1/2 z-0" />
          
          <div className="grid lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => {
              const Icon = step.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  {/* Step Card */}
                  <div className="card-modern p-8 text-center group hover:scale-105 transition-all duration-300">
                    {/* Step Number */}
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    
                    {/* Icon */}
                    <div className={`inline-flex p-6 rounded-2xl bg-gradient-to-r ${step.color} shadow-lg group-hover:shadow-xl transition-all duration-300 mb-6`}>
                      <Icon className="h-10 w-10 text-white" />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-xl font-bold text-white mb-4 group-hover:text-primary-400 transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  
                  {/* Arrow (Desktop) */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                      <div className="bg-white/10 backdrop-blur-lg rounded-full p-2">
                        <ArrowRight className="h-6 w-6 text-primary-400" />
                      </div>
                    </div>
                  )}
                </motion.div>
              )
            })}
          </div>
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-16 grid md:grid-cols-3 gap-8"
        >
          <div className="text-center">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <span className="text-white font-bold text-xl">✓</span>
            </div>
            <h4 className="text-lg font-semibold text-white mb-2">Números Únicos</h4>
            <p className="text-gray-300">Cada número se vende solo una vez, garantizando la exclusividad de tu participación.</p>
          </div>
          
          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <span className="text-white font-bold text-xl">⚡</span>
            </div>
            <h4 className="text-lg font-semibold text-white mb-2">Resultados Instantáneos</h4>
            <p className="text-gray-300">Conoce los resultados al momento de la membresía con total transparencia.</p>
          </div>
          
          <div className="text-center">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <span className="text-white font-bold text-xl">🔒</span>
            </div>
            <h4 className="text-lg font-semibold text-white mb-2">Pago Seguro</h4>
            <p className="text-gray-300">Procesamos todos los pagos con la máxima seguridad y encriptación.</p>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-primary text-lg px-8 py-4"
          >
            Comenzar Ahora
          </motion.button>
        </motion.div>
      </div>
    </section>
  )
}

export default HowItWorks